'use client';

import React, { useState, useEffect } from 'react';
import { categoriesApi, CategoryWithStats } from '@/app/api/categories';
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useToast } from '@/hooks/useToast';
import ConfirmDialog from '@/components/common/ConfirmDialog';

// Category templates
const CATEGORY_TEMPLATES = {
    blogger: [
        { name: 'blog', displayName: 'Blog' },
        { name: 'tutorials', displayName: 'Tutorials' },
        { name: 'reviews', displayName: 'Reviews' },
        { name: 'news', displayName: 'News' },
        { name: 'personal', displayName: 'Personal' },
        { name: 'travel', displayName: 'Travel' },
        { name: 'food', displayName: 'Food & Recipes' },
        { name: 'lifestyle', displayName: 'Lifestyle' },
    ],
    business: [
        { name: 'company-news', displayName: 'Company News' },
        { name: 'product-updates', displayName: 'Product Updates' },
        { name: 'case-studies', displayName: 'Case Studies' },
        { name: 'industry-insights', displayName: 'Industry Insights' },
        { name: 'how-to', displayName: 'How-To Guides' },
        { name: 'announcements', displayName: 'Announcements' },
        { name: 'resources', displayName: 'Resources' },
        { name: 'events', displayName: 'Events' },
    ],
};

export default function CategoriesManager() {
    const [categories, setCategories] = useState<CategoryWithStats[]>([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState<CategoryWithStats | null>(null);
    const [categoryToDelete, setCategoryToDelete] = useState<{ id: string; name: string } | null>(null);
    const { addToast } = useToast();

    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        try {
            setLoading(true);
            const data = await categoriesApi.getCategoryStats();
            setCategories(data);
        } catch (error) {
            addToast('Failed to load categories', 'error');
            console.error('Error loading categories:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (name: string, displayName: string) => {
        try {
            setActionLoading(true);
            await categoriesApi.createCategory(name, displayName);
            addToast('Category created successfully', 'success');
            await loadCategories();
            setShowCreateModal(false);
        } catch (error: any) {
            const errorMessage = error.response?.data?.error || 'Failed to create category';
            addToast(errorMessage, 'error');
            console.error('Error creating category:', error);
        } finally {
            setActionLoading(false);
        }
    };

    const handleUpdate = async (id: string, displayName: string) => {
        try {
            setActionLoading(true);
            await categoriesApi.updateCategory(id, displayName);
            addToast('Category updated successfully', 'success');
            await loadCategories();
            setEditingCategory(null);
        } catch (error) {
            addToast('Failed to update category', 'error');
            console.error('Error updating category:', error);
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteClick = (id: string, name: string, articleCount: number) => {
        if (articleCount > 0) {
            addToast(`Cannot delete category with ${articleCount} article(s)`, 'error');
            return;
        }
        setCategoryToDelete({ id, name });
    };

    const handleDeleteConfirm = async () => {
        if (!categoryToDelete) return;

        try {
            await categoriesApi.deleteCategory(categoryToDelete.id);
            addToast('Category deleted successfully', 'success');
            loadCategories();
            setCategoryToDelete(null);
        } catch (error: any) {
            const errorMessage = error.response?.data?.error || 'Failed to delete category';
            addToast(errorMessage, 'error');
            console.error('Error deleting category:', error);
        }
    };

    const handleCreateFromTemplate = async (selectedCategories: Array<{ name: string, displayName: string }>) => {
        try {
            setActionLoading(true);
            let successCount = 0;
            let skipCount = 0;
            let hasUnexpectedError = false;

            for (const cat of selectedCategories) {
                try {
                    await categoriesApi.createCategory(cat.name, cat.displayName);
                    successCount++;
                } catch (error: any) {
                    // Skip if category already exists (409 Conflict)
                    if (error.response?.status === 409) {
                        skipCount++;
                    } else {
                        // Unexpected error
                        hasUnexpectedError = true;
                        console.error(`Error creating category ${cat.name}:`, error);
                        throw error;
                    }
                }
            }

            if (hasUnexpectedError) {
                addToast('Some categories failed to create due to duplicates. Please reload the page and try again.', 'error');
                return;
            }

            if (successCount > 0) {
                addToast(`Created ${successCount} categor${successCount === 1 ? 'y' : 'ies'}${skipCount > 0 ? ` (${skipCount} skipped - already exist)` : ''}`, 'success');
            } else if (skipCount > 0) {
                addToast('All selected categories already exist', 'success');
            }

            await loadCategories();
            setShowTemplateModal(false);
        } catch (error) {
            addToast('Failed to create categories. Please reload the page and try again.', 'error');
            console.error('Error creating template categories:', error);
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                <div>
                    <h3 className="text-lg font-medium leading-6 text-gray-900">
                        Categories
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                        Create and organize categories for your articles
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setShowTemplateModal(true)}
                        className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-sm text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                    >
                        <PlusIcon className="w-4 h-4" />
                        Template
                    </button>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-sm text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                    >
                        <PlusIcon className="w-4 h-4" />
                        New
                    </button>
                </div>
            </div>

            <div className="border-t border-gray-200">
                {loading ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                ) : categories.length === 0 ? (
                    <div className="p-12 text-center">
                        <p className="text-gray-500 mb-4">No categories yet</p>
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="text-blue-600 hover:text-blue-700 font-medium"
                        >
                            Create your first category
                        </button>
                    </div>
                ) : (
                    <div className="overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Display Name
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Slug
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Articles
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {categories.map((category) => (
                                    <tr key={category.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">
                                                {category.display_name}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-500 font-mono">
                                                {category.name}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                {category.article_count}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button
                                                onClick={() => setEditingCategory(category)}
                                                className="text-blue-600 hover:text-blue-900 mr-4 inline-flex items-center gap-1"
                                                title="Edit category"
                                            >
                                                <PencilIcon className="w-4 h-4" />
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDeleteClick(category.id, category.display_name, category.article_count)}
                                                className={`inline-flex items-center gap-1 ${category.article_count > 0
                                                    ? 'text-gray-400 cursor-not-allowed'
                                                    : 'text-red-600 hover:text-red-900'
                                                    }`}
                                                disabled={category.article_count > 0}
                                                title={category.article_count > 0 ? 'Cannot delete category in use' : 'Delete category'}
                                            >
                                                <TrashIcon className="w-4 h-4" />
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Create Modal */}
            {showCreateModal && (
                <CategoryModal
                    title="Create New Category"
                    onClose={() => setShowCreateModal(false)}
                    onSubmit={handleCreate}
                    loading={actionLoading}
                />
            )}

            {/* Edit Modal */}
            {editingCategory && (
                <CategoryModal
                    title="Edit Category"
                    initialDisplayName={editingCategory.display_name}
                    onClose={() => setEditingCategory(null)}
                    onSubmit={(name, displayName) => handleUpdate(editingCategory.id, displayName)}
                    isEdit
                    loading={actionLoading}
                />
            )}

            {/* Template Modal */}
            {showTemplateModal && (
                <TemplateModal
                    onClose={() => setShowTemplateModal(false)}
                    onSubmit={handleCreateFromTemplate}
                    templates={CATEGORY_TEMPLATES}
                    existingCategories={categories.map(c => c.name)}
                    loading={actionLoading}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                isOpen={!!categoryToDelete}
                onClose={() => setCategoryToDelete(null)}
                onConfirm={handleDeleteConfirm}
                title="Delete Category"
                message={`Are you sure you want to delete "${categoryToDelete?.name}"? This action cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
            />
        </div>
    );
}

interface CategoryModalProps {
    title: string;
    initialDisplayName?: string;
    onClose: () => void;
    onSubmit: (name: string, displayName: string) => void;
    isEdit?: boolean;
    loading?: boolean;
}

function CategoryModal({ title, initialDisplayName = '', onClose, onSubmit, isEdit = false, loading = false }: CategoryModalProps) {
    const [displayName, setDisplayName] = useState(initialDisplayName);
    const [name, setName] = useState('');
    const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

    // Auto-generate slug from display name when field loses focus
    const handleDisplayNameBlur = () => {
        // Only auto-generate if:
        // 1. Not in edit mode
        // 2. Display name is not empty
        // 3. Slug hasn't been manually edited by user
        if (!isEdit && displayName.trim() && !slugManuallyEdited) {
            // Convert to lowercase, replace spaces with hyphens, remove special chars
            const autoSlug = displayName
                .toLowerCase()
                .trim()
                .replace(/\s+/g, '-')           // Replace spaces with hyphens
                .replace(/[^a-z0-9-]/g, '')     // Remove special characters
                .replace(/-+/g, '-')            // Replace multiple hyphens with single
                .replace(/^-|-$/g, '');         // Remove leading/trailing hyphens

            setName(autoSlug);
        }
    };

    // Track manual edits to slug field
    const handleSlugChange = (value: string) => {
        setName(value);
        setSlugManuallyEdited(true);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!displayName.trim() || loading) return;

        // For create, use the name field (auto-generated or manual); for edit, we don't change the slug
        onSubmit(isEdit ? '' : name || displayName, displayName.trim());
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-500 transition-colors"
                        disabled={loading}
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <div>
                        <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-1">
                            Display Name *
                        </label>
                        <input
                            type="text"
                            id="displayName"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            onBlur={handleDisplayNameBlur}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-60 disabled:bg-gray-50"
                            placeholder="e.g., Technology, Product Updates"
                            required
                            autoFocus
                            disabled={loading}
                        />
                        <p className="mt-1 text-xs text-gray-500">
                            This is how the category will appear in the UI
                        </p>
                    </div>

                    {!isEdit && (
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                Slug (auto-generated)
                            </label>
                            <input
                                type="text"
                                id="name"
                                value={name}
                                onChange={(e) => handleSlugChange(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm bg-gray-50 disabled:opacity-60"
                                placeholder="Auto-generated from display name"
                                disabled={loading}
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                Auto-generated from display name. You can edit if needed.
                            </p>
                        </div>
                    )}

                    <div className="flex gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex justify-center items-center disabled:opacity-60 disabled:cursor-not-allowed"
                            disabled={loading}
                        >
                            {loading ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                isEdit ? 'Update' : 'Create'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

interface TemplateModalProps {
    onClose: () => void;
    onSubmit: (selectedCategories: Array<{ name: string, displayName: string }>) => void;
    templates: Record<string, Array<{ name: string, displayName: string }>>;
    existingCategories: string[];
    loading?: boolean;
}

function TemplateModal({ onClose, onSubmit, templates, existingCategories, loading = false }: TemplateModalProps) {
    const [selectedTemplate, setSelectedTemplate] = useState<'blogger' | 'business' | null>(null);
    const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());

    const handleTemplateSelect = (template: 'blogger' | 'business') => {
        setSelectedTemplate(template);
        // Select all categories by default, except those that already exist
        const allNames = templates[template]
            .filter(cat => !existingCategories.includes(cat.name))
            .map(cat => cat.name);
        setSelectedCategories(new Set(allNames));
    };

    const toggleCategory = (name: string) => {
        if (loading) return;
        const newSelected = new Set(selectedCategories);
        if (newSelected.has(name)) {
            newSelected.delete(name);
        } else {
            newSelected.add(name);
        }
        setSelectedCategories(newSelected);
    };

    const handleSubmit = () => {
        if (!selectedTemplate || selectedCategories.size === 0 || loading) return;

        const categoriesToCreate = templates[selectedTemplate]
            .filter(cat => selectedCategories.has(cat.name));

        onSubmit(categoriesToCreate);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900">Add Categories from Template</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-500 transition-colors"
                        disabled={loading}
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto flex-1">
                    {!selectedTemplate ? (
                        <div className="space-y-4">
                            <p className="text-sm text-gray-600 mb-4">
                                Choose a template to quickly add pre-defined categories for your use case:
                            </p>

                            <button
                                onClick={() => handleTemplateSelect('blogger')}
                                className="w-full p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
                                disabled={loading}
                            >
                                <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600">
                                    Blogger Template
                                </h3>
                                <p className="text-sm text-gray-600 mb-3">
                                    Perfect for personal blogs, lifestyle content, and creative writing
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {templates.blogger.map(cat => (
                                        <span key={cat.name} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                            {cat.displayName}
                                        </span>
                                    ))}
                                </div>
                            </button>

                            <button
                                onClick={() => handleTemplateSelect('business')}
                                className="w-full p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
                                disabled={loading}
                            >
                                <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600">
                                    Business Template
                                </h3>
                                <p className="text-sm text-gray-600 mb-3">
                                    Ideal for company blogs, product updates, and professional content
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {templates.business.map(cat => (
                                        <span key={cat.name} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                            {cat.displayName}
                                        </span>
                                    ))}
                                </div>
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {selectedTemplate === 'blogger' ? 'Blogger' : 'Business'} Categories
                                </h3>
                                <button
                                    onClick={() => setSelectedTemplate(null)}
                                    className="text-sm text-blue-600 hover:text-blue-700"
                                    disabled={loading}
                                >
                                    ← Change Template
                                </button>
                            </div>

                            <p className="text-sm text-gray-600 mb-4">
                                Select the categories you want to create (existing categories are disabled):
                            </p>

                            <div className="space-y-2">
                                {templates[selectedTemplate].map(cat => {
                                    const alreadyExists = existingCategories.includes(cat.name);
                                    return (
                                        <label
                                            key={cat.name}
                                            className={`flex items-center p-3 border border-gray-200 rounded-lg ${alreadyExists
                                                ? 'bg-gray-50 opacity-60 cursor-not-allowed'
                                                : 'hover:bg-gray-50 cursor-pointer'
                                                }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedCategories.has(cat.name)}
                                                onChange={() => toggleCategory(cat.name)}
                                                disabled={alreadyExists || loading}
                                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                            />
                                            <div className="ml-3 flex-1">
                                                <div className="flex items-center gap-2">
                                                    <div className="text-sm font-medium text-gray-900">{cat.displayName}</div>
                                                    {alreadyExists && (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600">
                                                            Already exists
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-gray-500 font-mono">{cat.name}</div>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex gap-3 p-6 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    {selectedTemplate && (
                        <button
                            onClick={handleSubmit}
                            disabled={selectedCategories.size === 0 || loading}
                            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center"
                        >
                            {loading ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                `Create ${selectedCategories.size} Categor${selectedCategories.size === 1 ? 'y' : 'ies'}`
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
